<script setup lang="ts">
useSeoMeta({
  title: 'Industries — QikSolve',
  description:
    "See how QikSolve's quality systems and Microsoft 365 governance approach relates to life sciences, manufacturing, and service teams.",
});

const industries = [
  {
    chip: 'Pharmaceutical and life sciences',
    title: 'Pharmaceutical and life-sciences operations',
    needs: 'Controlled quality records, traceability, and audit readiness.',
    pathway: 'Quality systems and digital governance',
    links: [
      { label: 'Quality systems', to: '/product/quality-systems' },
      { label: 'Data and system governance', to: '/product/data-and-system-governance-for-quality-teams' },
    ],
  },
  {
    chip: 'Medical devices',
    title: 'Medical-device and regulated manufacturing',
    needs: 'Documented processes, evidence, and change control.',
    pathway: 'Quality systems and validation context',
    links: [
      { label: 'Quality systems', to: '/product/quality-systems' },
      { label: 'Regulatory compliance', to: '/product/regulatory-compliance' },
    ],
  },
  {
    chip: 'Food and beverage',
    title: 'Food, consumer, and certified manufacturing',
    needs: 'Consistent process control and auditable operations.',
    pathway: 'Operational quality and Microsoft 365 governance',
    links: [
      { label: 'Services', to: '/services' },
      { label: 'SharePoint governance', to: '/product/sharepoint-governance' },
    ],
  },
  {
    chip: null,
    title: 'Professional and operational service teams',
    needs: 'Controlled information, approval flows, and reporting.',
    pathway: 'SharePoint governance and quality dashboards',
    links: [
      { label: 'SharePoint governance', to: '/product/sharepoint-governance' },
      { label: 'Quality dashboards', to: '/product/quality-dashboards' },
    ],
  },
];

const relatedLinks = [
  { label: 'Explore by Need', description: 'Start from a real operational or quality question rather than an industry label.', to: '/explore-by-need' },
  { label: 'Services', description: 'See how QikSolve helps select, implement, operate, and improve a proportionate solution.', to: '/services' },
  { label: 'Products', description: 'Review the QikSolve portfolio and the Qx and INQ product pathways.', to: '/products' },
];
</script>

<template>
  <div class="bg-slate-50">
    <ArchetypeHero
      eyebrow="Industries"
      title="Context matters to good quality practice."
      summary="QikSolve supports quality-critical organisations of every size. The right pathway depends on your operating context, not a separate industry-specific product, so start with the context that best matches your operations."
      :cta="{ label: 'Start a conversation', to: '/contact' }"
    />

    <section class="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <div class="max-w-3xl">
        <p class="text-sm font-semibold uppercase tracking-wider text-brand-700">
          Operating context
        </p>
        <h2 class="mt-2 text-3xl font-heading font-bold text-gray-900 sm:text-4xl">
          Find the industry context closest to your operations.
        </h2>
        <p class="mt-5 text-lg leading-8 text-gray-700">
          These are planning contexts to help you find a relevant corporate pathway. They are not
          regulatory advice or an assertion that every organisation in a sector has the same
          obligations.
        </p>
      </div>

      <div class="mt-10 grid gap-6 md:grid-cols-2">
        <article
          v-for="industry in industries"
          :key="industry.title"
          class="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-[var(--shadow-card)]"
        >
          <span
            v-if="industry.chip"
            class="mb-3 inline-flex w-fit items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700"
          >
            {{ industry.chip }}
          </span>
          <h3 class="text-xl font-heading font-bold text-gray-900">
            {{ industry.title }}
          </h3>
          <p class="mt-3 text-gray-700">
            {{ industry.needs }}
          </p>
          <p class="mt-4 flex-1 border-t border-gray-100 pt-4 text-sm leading-6 text-gray-600">
            Relevant corporate pathway: {{ industry.pathway }}
          </p>
          <div class="mt-5 flex flex-wrap gap-x-5 gap-y-2">
            <NuxtLink
              v-for="link in industry.links"
              :key="link.to"
              :to="link.to"
              class="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              {{ link.label }}
              <UIcon
                name="i-heroicons-arrow-right-20-solid"
                class="size-4"
                aria-hidden="true"
              />
            </NuxtLink>
          </div>
        </article>
      </div>
    </section>

    <section class="mx-auto max-w-6xl space-y-10 px-4 pb-12 sm:px-6 sm:pb-16 lg:px-8">
      <ArchetypeCallout
        title="Planning context, not a regulatory determination"
        content="These contexts guide corporate content planning and are not regulatory advice or an assertion of suitability for every organisation in a sector. Confirm the applicable framework, jurisdiction, and intended use for your own operations. Product-specific workflows remain on the relevant Qx or INQ destination."
        tone="boundary"
      />

      <ArchetypeRelatedLinks
        title="Not sure where to start?"
        :items="relatedLinks"
      />

      <ArchetypeCta
        title="Bring your operating context to the conversation."
        content="Tell us about your organisation and current quality practice. We can help identify the proportionate next step."
        label="Contact QikSolve"
        to="/contact"
      />
    </section>
  </div>
</template>
